import axiosInstance from '../../../utils/axiosConfig';
import incomeService from '../../income/services/incomeService';

const API_URL = '/api/cashflow';

/**
 * Helper function to determine if we should add partner access headers
 * This checks if the current context suggests partner data access
 */
const shouldUsePartnerAccess = (options = {}) => {
  // Check if explicitly requesting partner data
  if (options.partnerAccess === true) {
    return true;
  }
  
  // Check URL parameters for partner access indicators
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('partner') === 'true' || urlParams.get('partnerAccess') === 'true') {
    return true;
  }
  
  // Check if current path suggests partner access
  const isPartnerPath = window.location.pathname.includes('/partner') || 
                       window.location.pathname.includes('/shared');
  
  return isPartnerPath;
};

/**
 * Helper function to add partner access headers if needed
 */
const getHeaders = (options = {}) => {
  const headers = {};
  
  if (shouldUsePartnerAccess(options)) {
    headers['x-partner-access'] = 'true';
    console.log('🤝 Adding partner access headers to cashflow request');
  }
  
  return headers;
};

/**
 * Fetches the cash flow summary data from the server
 * @param {string} userId - User ID for the data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The cash flow summary data
 */
export const getCashFlowSummary = async (userId, options = {}) => {
  try {
    // Fetch all required data in parallel
    const [expensesResponse, debtsResponse, savingsResponse, incomesResponse] = await Promise.all([
      getExpenses(options),
      getDebts(options),
      getSavings(options),
      incomeService.getAll(options)
    ]);

    // Extract data from responses and ensure they are arrays
    const expenses = Array.isArray(expensesResponse) ? expensesResponse : [];
    const debts = Array.isArray(debtsResponse) ? debtsResponse : [];
    const savings = Array.isArray(savingsResponse) ? savingsResponse : [];
    const incomes = Array.isArray(incomesResponse.data) ? incomesResponse.data : [];

    // Calculate monthly totals
    const totalMonthlyExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    const totalDebt = debts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
    const totalMonthlyDebtPayments = debts.reduce((sum, debt) => sum + (debt.monthlyPayment || 0), 0);
    const totalSavings = savings.reduce((sum, saving) => sum + (saving.amount || 0), 0);
    const totalSavingsTarget = savings.reduce((sum, saving) => sum + (saving.target || 0), 0);

    // Calculate income totals
    const monthlyGrossIncome = incomes.reduce((sum, income) => {
      const frequency = income.frequency?.toLowerCase() || 'monthly';
      const amount = income.amount || 0;
      
      switch (frequency) {
        case 'weekly':
          return sum + (amount * 52 / 12);
        case 'biweekly':
          return sum + (amount * 26 / 12);
        case 'semimonthly':
          return sum + (amount * 24 / 12);
        case 'monthly':
          return sum + amount;
        case 'quarterly':
          return sum + (amount / 3);
        case 'semiannually':
          return sum + (amount / 6);
        case 'annually':
          return sum + (amount / 12);
        default:
          return sum + amount;
      }
    }, 0);

    // Calculate net income (after taxes)
    const monthlyNetIncome = incomes.reduce((sum, income) => {
      const frequency = income.frequency.toLowerCase();
      const amount = income.amount;
      const taxRate = income.taxRate || 0;
      const netAmount = amount * (1 - taxRate);
      
      switch (frequency) {
        case 'weekly':
          return sum + (netAmount * 52 / 12);
        case 'biweekly':
          return sum + (netAmount * 26 / 12);
        case 'semimonthly':
          return sum + (netAmount * 24 / 12);
        case 'monthly':
          return sum + netAmount;
        case 'quarterly':
          return sum + (netAmount / 3);
        case 'semiannually':
          return sum + (netAmount / 6);
        case 'annually':
          return sum + (netAmount / 12);
        default:
          return sum;
      }
    }, 0);

    // Calculate savings progress
    const savingsProgress = totalSavingsTarget > 0 
      ? (totalSavings / totalSavingsTarget) * 100 
      : 0;

    // Get upcoming payments
    const upcomingPayments = [
      ...expenses.filter(expense => expense.dueDate > new Date()),
      ...debts.filter(debt => debt.nextPaymentDate > new Date())
    ].sort((a, b) => a.dueDate - b.dueDate);

    return {
      totalMonthlyExpenses,
      totalDebt,
      totalMonthlyDebtPayments,
      totalSavings,
      totalSavingsTarget,
      savingsProgress,
      upcomingPayments,
      monthlyGrossIncome,
      monthlyNetIncome
    };
  } catch (error) {
    console.error('Error getting cash flow summary:', error);
    throw error;
  }
};

/**
 * Fetches all expenses from the server
 * @param {Object} options - Options including partnerAccess and includePartner flags
 * @returns {Promise<Array>} Array of expense objects
 */
export const getExpenses = async (options = {}) => {
  try {
    let url = `${API_URL}/expenses`;
    if (options.partnerAccess) {
      url += '?partner=true';
    } else if (options.includePartner) {
      url += '?includePartner=true';
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

/**
 * Creates a new expense
 * @param {Object} expenseData - The expense data to create
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The created expense
 */
export const createExpense = async (expenseData, options = {}) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/expenses`, expenseData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

/**
 * Updates an existing expense
 * @param {string} id - The ID of the expense to update
 * @param {Object} expenseData - The updated expense data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The updated expense
 */
export const updateExpense = async (id, expenseData, options = {}) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/expenses/${id}`, expenseData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

/**
 * Deletes an expense
 * @param {string} id - The ID of the expense to delete
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The deleted expense
 */
export const deleteExpense = async (id, options = {}) => {
  try {
    await axiosInstance.delete(`${API_URL}/expenses/${id}`, {
      headers: getHeaders(options)
    });
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

/**
 * Fetches all debts from the server
 * @param {Object} options - Options including partnerAccess and includePartner flags
 * @returns {Promise<Array>} Array of debt objects
 */
export const getDebts = async (options = {}) => {
  try {
    let url = `${API_URL}/debts`;
    if (options.partnerAccess) {
      url += '?partner=true';
    } else if (options.includePartner) {
      url += '?includePartner=true';
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching debts:', error);
    throw error;
  }
};

/**
 * Creates a new debt
 * @param {Object} debtData - The debt data to create
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The created debt
 */
export const createDebt = async (debtData, options = {}) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/debts`, debtData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error creating debt:', error);
    throw error;
  }
};

/**
 * Updates an existing debt
 * @param {string} id - The ID of the debt to update
 * @param {Object} debtData - The updated debt data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The updated debt
 */
export const updateDebt = async (id, debtData, options = {}) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/debts/${id}`, debtData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating debt:', error);
    throw error;
  }
};

/**
 * Deletes a debt
 * @param {string} id - The ID of the debt to delete
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The deleted debt
 */
export const deleteDebt = async (id, options = {}) => {
  try {
    await axiosInstance.delete(`${API_URL}/debts/${id}`, {
      headers: getHeaders(options)
    });
  } catch (error) {
    console.error('Error deleting debt:', error);
    throw error;
  }
};

/**
 * Fetches all savings from the server
 * @param {Object} options - Options including partnerAccess and includePartner flags
 * @returns {Promise<Array>} Array of savings objects
 */
export const getSavings = async (options = {}) => {
  try {
    let url = `${API_URL}/savings`;
    if (options.partnerAccess) {
      url += '?partner=true';
    } else if (options.includePartner) {
      url += '?includePartner=true';
    }
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching savings:', error);
    throw error;
  }
};

/**
 * Creates a new savings goal
 * @param {Object} savingsData - The savings data to create
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The created savings
 */
export const createSavings = async (savingsData, options = {}) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/savings`, savingsData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error creating savings goal:', error);
    throw error;
  }
};

/**
 * Updates an existing savings goal
 * @param {string} id - The ID of the savings to update
 * @param {Object} savingsData - The updated savings data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The updated savings
 */
export const updateSavings = async (id, savingsData, options = {}) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/savings/${id}`, savingsData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating savings goal:', error);
    throw error;
  }
};

/**
 * Deletes a savings goal
 * @param {string} id - The ID of the savings to delete
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The deleted savings
 */
export const deleteSavings = async (id, options = {}) => {
  try {
    await axiosInstance.delete(`${API_URL}/savings/${id}`, {
      headers: getHeaders(options)
    });
  } catch (error) {
    console.error('Error deleting savings goal:', error);
    throw error;
  }
};

export const recordExpensePayment = async (id, paymentData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/expenses/${id}/payments`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error recording expense payment:', error);
    throw error;
  }
};

export const recordDebtPayment = async (id, paymentData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/debts/${id}/payments`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error recording debt payment:', error);
    throw error;
  }
};

export const recordSavingsContribution = async (id, contributionData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/savings/${id}/contributions`, contributionData);
    return response.data;
  } catch (error) {
    console.error('Error recording savings contribution:', error);
    throw error;
  }
};

/**
 * Fetches all assets from the server
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Array>} Array of asset objects
 */
export const getAssets = async (options = {}) => {
  try {
    console.log('🔍 getAssets: Making request to', `${API_URL}/assets`);
    const response = await axiosInstance.get(`${API_URL}/assets`, {
      headers: getHeaders(options)
    });
    console.log('🔍 getAssets: Raw response:', response);
    console.log('🔍 getAssets: Response status:', response.status);
    console.log('🔍 getAssets: Response data:', response.data);
    console.log('🔍 getAssets: Data type:', typeof response.data);
    console.log('🔍 getAssets: Is array?', Array.isArray(response.data));
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching assets:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    throw error;
  }
};

/**
 * Creates a new asset
 * @param {Object} assetData - The asset data to create
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The created asset
 */
export const createAsset = async (assetData, options = {}) => {
  try {
    console.log('🔍 createAsset: Making request to', `${API_URL}/assets`);
    console.log('🔍 createAsset: Request data:', assetData);
    const response = await axiosInstance.post(`${API_URL}/assets`, assetData, {
      headers: getHeaders(options)
    });
    console.log('🔍 createAsset: Raw response:', response);
    console.log('🔍 createAsset: Response status:', response.status);
    console.log('🔍 createAsset: Response data:', response.data);
    console.log('🔍 createAsset: Created asset ID:', response.data._id);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating asset:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
      requestData: assetData
    });
    throw error;
  }
};

/**
 * Updates an existing asset
 * @param {string} id - The ID of the asset to update
 * @param {Object} assetData - The updated asset data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The updated asset
 */
export const updateAsset = async (id, assetData, options = {}) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/assets/${id}`, assetData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error updating asset:', error);
    throw error;
  }
};

/**
 * Deletes an asset
 * @param {string} id - The ID of the asset to delete
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The deleted asset
 */
export const deleteAsset = async (id, options = {}) => {
  try {
    await axiosInstance.delete(`${API_URL}/assets/${id}`, {
      headers: getHeaders(options)
    });
  } catch (error) {
    console.error('Error deleting asset:', error);
    throw error;
  }
};

/**
 * Records an asset value update
 * @param {string} id - The ID of the asset
 * @param {Object} updateData - The value update data
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Object>} The updated asset
 */
export const recordAssetValueUpdate = async (id, updateData, options = {}) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/assets/${id}/value-updates`, updateData, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error recording asset value update:', error);
    throw error;
  }
};

/**
 * Gets asset value history
 * @param {string} id - The ID of the asset
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise<Array>} Array of value history records
 */
export const getAssetValueHistory = async (id, options = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/assets/${id}/value-history`, {
      headers: getHeaders(options)
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching asset value history:', error);
    throw error;
  }
}; 